import moment from "moment";
import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";

import useRemoteDomain from "../../../../hooks/queryResults/remote/useRemoteDomain";
import useRemoteSource from "../../../../hooks/queryResults/remote/useRemoteSource";
import useRemoteTemplate from "../../../../hooks/queryResults/remote/useRemoteTemplate";
import useUrls from "../../../../hooks/useUrls";
import http from "../../../../services/httpService";
import Util from "../../../../util/util";
import Expire from "../../../ui/Expire";
import TimeAgo from "../../../ui/TimeAgo";
import TimeUntil from "../../../ui/TimeUntil";
import AdminRemoteSchedulesTable from "./AdminRemoteSchedulesTable";
import Table from "../../../layout/Table";

export default function AdminRemoteSource() {
  const domain = useRemoteDomain();
  const template = useRemoteTemplate();
  const source = useRemoteSource();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState("");
  const { apiUrl } = useUrls();

  const mutation = useMutation(
    () => {
      const data = {};
      const url = `${apiUrl}/admin/remote/sources/${source?.id}/pull`;
      const config = Util.addTokenToHeaders();
      console.log(`sending POST request to url ${url} with data:`, data);
      return http.post(url, data, config);
    },
    {
      onSuccess: async () => {
        console.log(`source pull - mutation success`);
        const queryKey = ["admin", "remote", "sources", template?.slug];
        await queryClient.invalidateQueries(queryKey);
        await queryClient.refetchQueries(queryKey);
        setTimeout(async () => {
          console.log("fetching queries again");
          await queryClient.refetchQueries(queryKey);
        }, 5000);
      },
      onError: (error) => {
        setErrorMessage(error.response.data.error);
      },
    }
  );

  const pullDeleteMutation = useMutation(
    (pullId) => {
      const url = `${apiUrl}/admin/docs/RemotePull/${pullId}`;
      const config = Util.addTokenToHeaders();
      console.log(`sending DELETE request to url ${url}`);
      return http.delete(url, config);
    },
    {
      onSuccess: async () => {
        console.log(`delete pull - mutation success`);
        const queryKey = ["admin", "remote", "sources", template?.slug];
        await queryClient.invalidateQueries(queryKey);
        await queryClient.refetchQueries(queryKey);
      },
      onError: (error) => {
        setErrorMessage(error.response.data.error);
      },
    }
  );

  const infoColumns = useMemo(
    () => [
      { Header: "Key", accessor: "key" },
      { Header: "Value", accessor: "value" },
    ],
    []
  );

  const infoData = useMemo(() => {
    let data = [];

    if (source) {
      const schedules = Object.keys(source?.schedules).map(
        (scheduleId) => source?.schedules[scheduleId]
      );
      let lastRun = schedules.reduce((prev, schedule) => {
        if (!prev) return schedule.lastRun;
        return moment(schedule.lastRun).isAfter(prev) ? schedule.lastRun : prev;
      }, null);
      const nextRun = schedules.reduce((prev, schedule) => {
        if (!prev) return schedule.nextRun;
        return moment(schedule.nextRun).isBefore(prev)
          ? schedule.nextRun
          : prev;
      }, null);

      if (!lastRun && source?.pulls && source?.pulls.length) {
        lastRun = source?.pulls[0].all.endDate;
      }

      data = [
        { key: "Progress", value: Util.getProgressIcon(source?.progress) },
        { key: "Status", value: source?.status },
        { key: "Priority", value: source?.priority },
        { key: "Last Run", value: <TimeAgo date={lastRun} /> },
        {
          key: "Next Run",
          value: <TimeUntil date={nextRun} />,
        },
      ];
    }
    return data;
  }, [source]);

  const pullsColumns = useMemo(
    () => [
      { Header: "Id", accessor: "id" },
      { Header: "Started", accessor: "startDate" },
      { Header: "Took", accessor: "took" },
      { Header: "All", accessor: "all" },
      { Header: "Fetch", accessor: "fetch" },
      { Header: "Parse", accessor: "parse" },
      { Header: "Check", accessor: "check" },
      { Header: "Add", accessor: "add" },
      { Header: "Compare", accessor: "compare" },
      { Header: "Delete", accessor: "delete" },
    ],
    []
  );

  const pullsData = useMemo(() => {
    const getPullsData = (source) => {
      const data = [];
      if (source) {
        source.pulls.forEach((pull) => {
          const row = {
            id: (
              <Link
                to={`/admin/remote/${domain.slug}/${template.slug}/${source.id}/${pull.id}`}
              >
                {pull.id}
              </Link>
            ),
            startDate: <TimeAgo date={pull.all.startDate} />,
            took: Util.formatTimeInSeconds(pull.all.took),
          };
          ["all", "fetch", "parse", "check", "add", "compare"].forEach(
            (phaseName) => {
              const phase = pull[phaseName];
              const took = Util.formatTimeInSeconds(phase.took);
              row[phaseName] = (
                <span title={took}>
                  <Link
                    style={{ textDecoration: "none" }}
                    to={`/admin/remote/${domain.slug}/${template.slug}/${source.id}/${pull.id}`}
                  >
                    {Util.getProgressIcon(phase.progress)}
                  </Link>
                </span>
              );
            }
          );
          row.delete = (
            <button
              onClick={() => pullDeleteMutation.mutate(pull.id)}
              className={"btn btn-alert"}
            >
              delete
            </button>
          );

          data.push(row);
        });
      }

      return data;
    };
    return getPullsData(source);
  }, [source, domain, template, pullDeleteMutation]);

  if (source && template && domain) {
    console.log(source);
    return (
      <>
        <h1>
          <Link to={"/admin"}>Admin</Link> -{" "}
          <Link to={"/admin/remote"}>Remote Data</Link> -{" "}
          <Link to={`/admin/remote/${domain.slug}`}>{domain.name}</Link> -{" "}
          <Link to={`/admin/remote/${domain.slug}/${template.slug}`}>
            {template.name}
          </Link>{" "}
          - Source
        </h1>
        <h2>
          {source.id} - <a href={source.url}>{source.url}</a>
        </h2>
        <button
          className={"btn"}
          onClick={(e) => {
            e.preventDefault();
            mutation.mutate({});
          }}
        >
          Pull Now
        </button>{" "}
        {mutation.isLoading ? (
          "pulling now..."
        ) : mutation.isSuccess ? (
          <Expire delay={3000}>success</Expire>
        ) : errorMessage ? (
          <span className={"alert"}>ERROR: {errorMessage}</span>
        ) : (
          ""
        )}
        <div style={{ display: "flex" }}>
          <div style={{ marginRight: "2rem" }}>
            <h2>Info</h2>
            <Table columns={infoColumns} data={infoData} />
          </div>
          <div>
            <AdminRemoteSchedulesTable schedules={source.schedules} />
          </div>
        </div>
        <h2>Recent Pulls</h2>
        <Table columns={pullsColumns} data={pullsData} />
      </>
    );
  }

  return null;
}
